import { ActionIcon, Alert, Box, Checkbox, Code, CopyButton, List, Stack, Text } from '@mantine/core';
import { Check, Copy, Info, ShieldAlert } from 'lucide-react';
import type { ProviderSetupInstructions } from '@bmai/shared';

interface Props {
    instructions: ProviderSetupInstructions;
    acknowledged: boolean;
    showAcknowledgement: boolean;
    onAcknowledgedChange: (value: boolean) => void;
}

interface BodySegment {
    kind: 'text' | 'code';
    content: string;
}

/** Splits a step body on ``` fences: odd segments render as copyable code blocks, the rest as text. */
function splitBodySegments(body: string): BodySegment[] {
    return body
        .split('```')
        .map((part, i): BodySegment => ({ kind: i % 2 === 1 ? 'code' : 'text', content: part.replace(/^\n+|\s+$/g, '') }))
        .filter((segment) => segment.content.length > 0);
}

export default function ConnectionStepPrerequisites({
    instructions,
    acknowledged,
    showAcknowledgement,
    onAcknowledgedChange,
}: Props): React.ReactElement {
    const severity = instructions.severity ?? 'info';
    const color = severity === 'warning' ? 'yellow' : 'blue';
    const Icon = severity === 'warning' ? ShieldAlert : Info;

    return (
        <Stack gap="lg">
            <Alert
                variant="light"
                color={color}
                icon={<Icon size={18} />}
                title={<Text fw={700} size="sm">{instructions.title}</Text>}
            >
                <Stack gap="md" mt={4}>
                    {instructions.intro && (
                        <Text size="sm" lh={1.6}>
                            {instructions.intro}
                        </Text>
                    )}
                    {instructions.steps && instructions.steps.length > 0 && (
                        <List
                            type="ordered"
                            spacing="md"
                            size="sm"
                            style={{ listStylePosition: 'outside', paddingLeft: '1.375rem' }}
                            styles={{ itemWrapper: { maxWidth: '100%' }, itemLabel: { maxWidth: '100%' } }}
                        >
                            {instructions.steps.map((step, i) => (
                                <List.Item key={i}>
                                    <Stack gap={4}>
                                        <Text fw={600} size="sm">{step.heading}</Text>
                                        {splitBodySegments(step.body).map((segment, j) =>
                                            segment.kind === 'code' ? (
                                                <Box key={j} pos="relative" mt={4}>
                                                    <Code block fz="xs" pr={40} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                                        {segment.content}
                                                    </Code>
                                                    <CopyButton value={segment.content}>
                                                        {({ copied, copy }) => (
                                                            <ActionIcon
                                                                variant="subtle"
                                                                color={copied ? 'teal' : 'gray'}
                                                                onClick={copy}
                                                                pos="absolute"
                                                                top={6}
                                                                right={6}
                                                                aria-label="Copy command"
                                                            >
                                                                {copied ? <Check size={14} /> : <Copy size={14} />}
                                                            </ActionIcon>
                                                        )}
                                                    </CopyButton>
                                                </Box>
                                            ) : (
                                                <Text key={j} size="sm" c="dimmed" lh={1.6} style={{ whiteSpace: 'pre-wrap' }}>
                                                    {segment.content}
                                                </Text>
                                            ),
                                        )}
                                    </Stack>
                                </List.Item>
                            ))}
                        </List>
                    )}
                </Stack>
            </Alert>
            {showAcknowledgement && (
                <Checkbox
                    checked={acknowledged}
                    onChange={(e) => onAcknowledgedChange(e.currentTarget.checked)}
                    label="I've completed the steps above"
                    description="Required before continuing"
                />
            )}
        </Stack>
    );
}
